import React, { useState, useEffect } from 'react';
import { 
  IconButton, 
  Badge, 
  Menu, 
  MenuItem, 
  ListItemText, 
  ListItemIcon, 
  Typography, 
  Divider, 
  Box,
  Button,
  Stack,
  Tooltip,
  CircularProgress,
  alpha,
  Drawer,
  Tab,
  Tabs,
  Paper,
  List,
  ListItem,
  Chip,
  SwipeableDrawer,
  useMediaQuery
} from '@mui/material';
import { 
  Notifications as NotificationsIcon, 
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  AccessTime as AccessTimeIcon,
  EventNote as EventNoteIcon,
  Payment as PaymentIcon,
  ListAlt as ListAltIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  FilterList as FilterListIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { DueDate, getDueDates, updateNotificationStatus, getCompletedNotifications } from '../../utils/notifications';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';

// Create sound for notification alerts
const NOTIFICATION_SOUND = typeof Audio !== 'undefined' 
  ? new Audio('/notification.mp3') 
  : null;

// Animation variants for notification icon
const iconAnimation = {
  pulse: {
    scale: [1, 1.15, 1],
    opacity: [0.7, 1, 0.7],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

const NotificationsMenu: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<DueDate[]>([]);
  const [completedNotifications, setCompletedNotifications] = useState<DueDate[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingCompleted, setLoadingCompleted] = useState(false);
  const [alertActive, setAlertActive] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const { currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const open = Boolean(anchorEl);
  
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    // Reset alert when opened
    setAlertActive(false);
    if (NOTIFICATION_SOUND) {
      NOTIFICATION_SOUND.pause();
      NOTIFICATION_SOUND.currentTime = 0;
    }
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerOpen = () => {
    handleClose(); // Close the menu
    setDrawerOpen(true);
    fetchCompletedNotifications();
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 1 && completedNotifications.length === 0) {
      fetchCompletedNotifications();
    }
  };

  const fetchNotifications = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      const dueDates = await getDueDates(currentUser.uid);
      // Filter out completed notifications from the main view
      const pendingNotifications = dueDates.filter(notification => !notification.completed);
      setNotifications(pendingNotifications);
      
      // Check if we should sound an alert
      const overdueOrDue = pendingNotifications.filter(
        notification => 
          (notification.status === 'overdue' || notification.status === 'due')
      );
      
      if (overdueOrDue.length > 0 && !open && !alertActive) {
        setAlertActive(true);
        playAlertSound();
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedNotifications = async () => {
    if (!currentUser) return;
    
    setLoadingCompleted(true);
    try {
      const completed = await getCompletedNotifications(currentUser.uid);
      setCompletedNotifications(completed);
    } catch (error) {
      console.error("Error fetching completed notifications:", error);
    } finally {
      setLoadingCompleted(false);
    }
  };

  const playAlertSound = () => {
    if (NOTIFICATION_SOUND) {
      NOTIFICATION_SOUND.play().catch(error => console.error("Error playing notification sound:", error));
      
      // Set a 5-second timeout to stop the sound
      setTimeout(() => {
        if (NOTIFICATION_SOUND) {
          NOTIFICATION_SOUND.pause();
          NOTIFICATION_SOUND.currentTime = 0;
        }
      }, 5000);
    }
  };

  const handleMarkComplete = async (id: string) => {
    try {
      await updateNotificationStatus(id, 'completed');
      // Remove from active notifications
      setNotifications(prev => prev.filter(n => n.id !== id));
      // Refresh completed notifications if drawer is open
      if (drawerOpen && tabValue === 1) {
        fetchCompletedNotifications();
      }
    } catch (error) {
      console.error("Error marking notification as complete:", error);
    }
  };

  const handleRemindLater = async (id: string) => {
    try {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      await updateNotificationStatus(id, 'pending', tomorrow);
      // Remove from active notifications
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    } catch (error) {
      console.error("Error setting reminder for later:", error);
    }
  };

  // Get icon based on notification type
  const getNotificationIcon = (type: string, status: string) => {
    if (status === 'completed') {
      return <CheckCircleIcon color="success" />;
    }
    
    switch (type) {
      case 'tax_filing':
        return <ListAltIcon color={status === 'overdue' ? 'error' : 'primary'} />;
      case 'payment':
        return <PaymentIcon color={status === 'overdue' ? 'error' : 'primary'} />;
      case 'receivable':
        return <PaymentIcon color={status === 'overdue' ? 'error' : 'primary'} />;
      case 'payable':
        return <PaymentIcon color={status === 'overdue' ? 'error' : 'primary'} />;
      case 'inventory':
        return <InfoIcon color={status === 'overdue' ? 'error' : 'primary'} />;
      default:
        return <EventNoteIcon color={status === 'overdue' ? 'error' : 'primary'} />;
    }
  };

  // Get text based on status
  const getStatusText = (status: string) => {
    switch (status) {
      case 'overdue':
        return <Chip size="small" label="Overdue" color="error" />;
      case 'due':
        return <Chip size="small" label="Due soon" color="warning" />;
      case 'upcoming':
        return <Chip size="small" label="Upcoming" color="info" />;
      case 'completed':
        return <Chip size="small" label="Completed" color="success" />;
      default:
        return null;
    }
  };

  // Format date
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Load notifications when component mounts and periodically
  useEffect(() => {
    if (currentUser) {
      fetchNotifications();
      
      // Refresh every minute
      const intervalId = setInterval(fetchNotifications, 60000);
      
      return () => {
        clearInterval(intervalId);
        // Clean up sound
        if (NOTIFICATION_SOUND) {
          NOTIFICATION_SOUND.pause();
          NOTIFICATION_SOUND.currentTime = 0;
        }
      };
    }
  }, [currentUser]);

  const pendingCount = notifications.filter(
    notification => 
      (notification.status === 'overdue' || notification.status === 'due')
  ).length;

  // Drawer content for notifications
  const renderNotificationDrawer = () => {
    return (
      <Box sx={{ width: isMobile ? '100vw' : 400, p: 0 }}>
        <Paper elevation={0} sx={{ position: 'sticky', top: 0, zIndex: 5, bgcolor: 'background.paper' }}>
          <Box sx={{ 
            p: 2, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              Notifications
            </Typography>
            <IconButton size="small" onClick={handleDrawerClose} color="inherit">
              {isMobile ? <ArrowBackIcon /> : <CloseIcon />}
            </IconButton>
          </Box>
          
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
          >
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <EventNoteIcon sx={{ mr: 1, fontSize: '1rem' }} />
                  <Typography>Active</Typography>
                  {pendingCount > 0 && (
                    <Chip 
                      size="small" 
                      label={pendingCount} 
                      color="error" 
                      sx={{ ml: 1, height: 20, minWidth: 20 }} 
                    />
                  )}
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <HistoryIcon sx={{ mr: 1, fontSize: '1rem' }} />
                  <Typography>History</Typography>
                </Box>
              } 
            />
          </Tabs>
        </Paper>

        <Box role="tabpanel" hidden={tabValue !== 0} sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={30} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No pending notifications
              </Typography>
              <Button 
                variant="outlined" 
                color="primary" 
                size="small" 
                startIcon={<FilterListIcon />}
                sx={{ mt: 2 }}
                onClick={() => setTabValue(1)}
              >
                View completed tasks
              </Button>
            </Box>
          ) : (
            <List disablePadding>
              {notifications.map(notification => (
                <ListItem 
                  key={notification.id}
                  disablePadding
                  sx={{ 
                    display: 'block',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    '&:last-child': {
                      borderBottom: 'none'
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      p: 2, 
                      borderLeft: `4px solid ${
                        notification.status === 'overdue' 
                          ? theme.palette.error.main 
                          : notification.status === 'due'
                          ? theme.palette.warning.main
                          : theme.palette.primary.main
                      }`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {getNotificationIcon(notification.type, notification.status)}
                      </ListItemIcon>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={500}>
                          {notification.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {notification.description}
                        </Typography>
                        
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mt: 1
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <AccessTimeIcon sx={{ fontSize: '0.9rem', color: 'text.secondary', mr: 0.5 }} />
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(notification.dueDate)}
                            </Typography>
                          </Box>
                          {getStatusText(notification.status)}
                        </Box>
                      </Box>
                    </Box>
                    
                    {(notification.status === 'overdue' || notification.status === 'due') && (
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'flex-end', 
                          mt: 1,
                          gap: 1
                        }}
                      >
                        <Button 
                          variant="outlined" 
                          size="small"
                          onClick={() => handleRemindLater(notification.id)}
                        >
                          Remind Later
                        </Button>
                        <Button 
                          variant="contained" 
                          size="small"
                          color="primary"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => handleMarkComplete(notification.id)}
                        >
                          Mark Complete
                        </Button>
                      </Box>
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        <Box role="tabpanel" hidden={tabValue !== 1} sx={{ p: 0 }}>
          {loadingCompleted ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={30} />
            </Box>
          ) : completedNotifications.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No completed tasks found
              </Typography>
              <Button 
                variant="outlined" 
                color="primary" 
                size="small" 
                sx={{ mt: 2 }}
                onClick={() => setTabValue(0)}
              >
                View active tasks
              </Button>
            </Box>
          ) : (
            <List disablePadding>
              {completedNotifications.map(notification => (
                <ListItem 
                  key={notification.id}
                  disablePadding
                  sx={{ 
                    display: 'block',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    backgroundColor: alpha(theme.palette.success.main, 0.04),
                    '&:last-child': {
                      borderBottom: 'none'
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      p: 2, 
                      borderLeft: `4px solid ${theme.palette.success.main}`,
                      opacity: 0.8
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircleIcon color="success" />
                      </ListItemIcon>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ textDecoration: 'line-through' }}>
                          {notification.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {notification.description}
                        </Typography>
                        
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          mt: 1
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <AccessTimeIcon sx={{ fontSize: '0.9rem', color: 'text.secondary', mr: 0.5 }} />
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(notification.dueDate)}
                            </Typography>
                          </Box>
                          <Chip size="small" label="Completed" color="success" />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <motion.div
        animate={alertActive ? "pulse" : "initial"}
        variants={iconAnimation}
        style={{ display: 'inline-flex' }}
      >
        <Tooltip title="Notifications">
          <IconButton
            onClick={handleClick}
            size="large"
            aria-label={`show ${pendingCount} new notifications`}
            color="inherit"
            sx={{
              backgroundColor: theme => alpha(theme.palette.primary.main, 0.08),
              '&:hover': { backgroundColor: theme => alpha(theme.palette.primary.main, 0.16) }
            }}
          >
            <Badge badgeContent={pendingCount} color="error">
              <NotificationsIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
      </motion.div>

      <Menu
        anchorEl={anchorEl}
        id="notifications-menu"
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            width: 350,
            maxHeight: 450,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="h6">Notifications</Typography>
          <Typography variant="body2" color="text.secondary">
            {pendingCount === 0 
              ? 'No pending tasks' 
              : `${pendingCount} pending task${pendingCount === 1 ? '' : 's'}`}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No pending notifications
            </Typography>
          </Box>
        ) : (
          <>
            {notifications.slice(0, 3).map((notification) => (
              <MenuItem 
                key={notification.id} 
                sx={{ 
                  py: 1.5, 
                  px: 2, 
                  borderLeft: `3px solid ${
                    notification.status === 'overdue' 
                      ? theme.palette.error.main 
                      : notification.status === 'due'
                      ? theme.palette.warning.main
                      : theme.palette.primary.main
                  }`,
                  flexDirection: 'column',
                  alignItems: 'flex-start'
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" width="100%">
                  <ListItemIcon>
                    {getNotificationIcon(notification.type, notification.status)}
                  </ListItemIcon>
                  <ListItemText 
                    primary={notification.title}
                    secondary={
                      <React.Fragment>
                        <Typography variant="body2" component="span" noWrap sx={{ display: 'block', maxWidth: 250 }}>
                          {notification.description}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <AccessTimeIcon fontSize="small" />
                          <Typography variant="caption">
                            {formatDate(notification.dueDate)}
                          </Typography>
                        </Stack>
                      </React.Fragment>
                    }
                  />
                </Stack>
                
                {(notification.status === 'overdue' || notification.status === 'due') && (
                  <Stack 
                    direction="row" 
                    spacing={1} 
                    sx={{ mt: 1, width: '100%', justifyContent: 'flex-end' }}
                  >
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={() => handleRemindLater(notification.id)}
                    >
                      Later
                    </Button>
                    <Button 
                      variant="contained" 
                      size="small"
                      color="primary"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => handleMarkComplete(notification.id)}
                    >
                      Done
                    </Button>
                  </Stack>
                )}
              </MenuItem>
            ))}
            
            {notifications.length > 3 && (
              <Box sx={{ p: 1, textAlign: 'center', borderTop: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="body2" color="text.secondary">
                  {notifications.length - 3} more notification{notifications.length - 3 !== 1 ? 's' : ''}
                </Typography>
              </Box>
            )}
            
            <Divider />
            <Box sx={{ p: 1, textAlign: 'center' }}>
              <Button 
                size="small" 
                onClick={handleDrawerOpen}
                variant="contained"
                fullWidth
              >
                View All Notifications
              </Button>
            </Box>
          </>
        )}
      </Menu>

      <SwipeableDrawer
        anchor={isMobile ? "right" : "right"}
        open={drawerOpen}
        onClose={handleDrawerClose}
        onOpen={() => setDrawerOpen(true)}
        sx={{
          '& .MuiDrawer-paper': {
            width: isMobile ? '100%' : 400,
            boxSizing: 'border-box',
          },
        }}
      >
        {renderNotificationDrawer()}
      </SwipeableDrawer>
    </>
  );
};

export default NotificationsMenu; 